RivalIQ.module('universal', function (universal, App, Backbone, Marionette, $, _) {

    'use strict';

    const UniversalMetricMixin = universal._Mixin = {
        getValue: function (options) {
            const me = this;
            const opts = _.extend({
                format: true
            }, options);
            return me._formatValue(me.data, opts);
        },
        getName: function (options) {
            const me = this;

            let name = me.metadata.name;
            if (me.get('property') !== 'name') {
                const dimensions = _.get(options, ['dimensions'], []);
                name = RivalIQ.metric.names.clarifyName(me.attributes, name, dimensions);
            }

            return name;
        },
        getMetadata: function () {
            const me = this;
            return _.clone(me.metadata);
        },
        getEarliestDataDate: function (data) {
            const me = this;

            return _.get(me.metadata, 'earliestDataDate');
        },
        getCalculationMetadata: function (data) {
            const me = this;

            if (me.get('benchmarkCompaniesFilter')) {
                return _.get(me.metadata, ['calculationMetadata']);
            } else {
                if (_.isUndefined(data)) {
                    data = [];
                }

                if (!_.isArray(data)) {
                    data = [data];
                }
                return _.get(me.metadata, ['calculationMetadata'].concat(data));
            }
        },
        getDescription: function () {
            const me = this;
            return me.metadata.description;
        },
        clone: function (attributes) {
            const me = this;

            const clone = RivalIQ.models.Metric.prototype.clone.call(me, attributes);

            let data = me.data;
            const freeDimensions = me.getFreeDimensions();
            if (!_.isEmpty(freeDimensions)) {
                const newFreeDimensions = clone.getFreeDimensions();
                _.each(_.difference(freeDimensions, newFreeDimensions), function (freeDimension) {
                    const dimensionValue = clone.get(freeDimension);
                    const index = RivalIQ.metric.freeDimensionIndex(me.attributes, freeDimension);
                    data = _.find(data, function (row) {
                        return (row[index] === dimensionValue);
                    });
                });
            }

            return _.extend(clone, UniversalMetricMixin, {
                data: data,
                metadata: me.metadata
            });
        }
    };

    universal.METRIC_ATTRIBUTES_ALLOWED = [
        'dimensions',
        'exclusions',
        'exclusionDimension',
        'focusCompanyId',
        'landscapeId',
        'limit',
        'orderBy',
        'property',
        'sortOrder',
        'source',
        'companySetDescriptor',
        'timeAnalysisMode',
        'timePeriod',
        'timezone',
        // NOTE this is currently inspected on the server in narrow cases, so
        // we need to include it. one day, maybe, it could be removed entirely.
        'useUniversalMetricsApi',
        'benchmarkCompaniesFilter',
        'aggregate',
        'over',
        'companyIdAggregate',
        'companySetIdAggregate',
        'onlyCompanySetIds',

        // Post metric attributes (note this is duplicated in getIdentityForUniversalApi)
        'objectType',
        'searchTerm',
        'channel',
        'mediaTypeFilter',
        'tweetTypeFilter',
        'postTagIds',
        'postTagConjunction',
        'postTagExclude'
    ];

    universal.sanitizeMetricForXhr = function (definition) {
        const attributes = _.uniq(universal.METRIC_ATTRIBUTES_ALLOWED.concat(definition.dimensions));
        return _.pick(definition, attributes);
    };


    universal._fetch = function (definitions) {
        if (_.isEmpty(definitions)) {
            return $.when([]);
        }

        const sanitized = _.map(definitions, universal.sanitizeMetricForXhr);
        const options = {
            contentType: 'application/json',
            data: JSON.stringify(sanitized),
            type: 'POST',
            url: '/api/metrics'
        };

        return $.ajax(options).then(function (results) {
            return _.map(results, function (result, i) {
                const metric = new RivalIQ.models.Metric(definitions[i]);
                return _.extend(metric, UniversalMetricMixin, result);
            });
        });
    };

    function fetchUniversalMetrics(attrs, definitions) {
        const isArray = _.isArray(definitions);

        const array = (isArray ? definitions : [definitions]);
        const appliedDefinitions = _.map(array, function (definition) {
            const attributes = _.extend({}, definition, attrs);
            const model = new RivalIQ.models.MetricDefinition(attributes);
            return _.defaults({
                _universalApiCacheKey: model.getIdentityForUniversalApi()
            }, attributes);
        });

        const data = _.reduce(appliedDefinitions, function (memo, definition, i) {
            const cached = universal.data.Metric.get(definition);
            if (cached) {
                memo.cached[i] = cached;
            } else {
                memo.fetch.push(definition);
                // NOTE we need to add a pending cache request so that
                // subsequent requests that would match will match (i.e., we
                // don't want to make more requests than we have to)
                universal.data.Metric.create(definition);
            }
            return memo;
        }, {
            cached: {},
            fetch: []
        });

        const cached = _.reduce(data.cached, function (memo, cache, idx) {
            return memo.then(function (models) {
                return cache.deferred.then(function (model) {
                    // NOTE we end up cloning the model here so that if the
                    // downstream handler wants to throw these things into a
                    // Backbone collection we will generate new `ids` (thus
                    // sidestepping the default behavior to collapse models in
                    // the collection by `id`)
                    return _.set(models, idx, model.clone());
                });
            });
        }, $.when({}));

        return $.when(universal._fetch(data.fetch))
            // NOTE we need to fetch the metrics first so that we can resolve
            // the pending requests in the cache
            .then(function (metrics) {
                _.each(metrics, function (metric) {
                    const cache = universal.data.Metric.get(metric.attributes);
                    if (cache && cache.deferred) {
                        cache.deferred.resolve(metric);
                    }
                });

                return $.when(cached, $.when(metrics));
            })
            .then(function (cached, metrics) {
                _.each(cached, function (metric, index) {
                    metrics.splice(index, 0, metric);
                });

                return isArray ? metrics : _.head(metrics);
            })
            .fail(function (error) {
                RivalIQ.execute('cacheLayer:load:fail', error);
            });
    }

    function fetchUniversalMetricsBuffered(buffer) {
        const definitions = _.reduce(buffer, function (memo, config) {
            const built = _.map([].concat(config.definitions), function (definition) {
                return _.extend({}, definition, config.attributes);
            });
            return memo.concat(built);
        }, []);

        if (_.isEmpty(definitions)) {
            return;
        }

        fetchUniversalMetrics(null, definitions).then(function (models) {
            _.each(buffer, function (config) {
                const isArray = _.isArray(config.definitions);
                const metrics = models.splice(0, _.size([].concat(config.definitions)));
                const result = (isArray ? new RivalIQ.collections.Metrics(metrics) : _.head(metrics));

                if (config && config.deferred) {
                    config.deferred.resolve(result);
                }
            });

            buffer.deferred.resolve(true);
        });
    }

    function createUniveralMetricsBuffer() {
        const buffer = _.extend([], Backbone.Events);
        buffer.deferred = new $.Deferred();
        buffer.listenTo(buffer, 'fetch', fetchUniversalMetricsBuffered);
        return buffer;
    }

    universal.fetch = function (attrs, definitions) {
        const me = this;

        if (_.isEmpty(definitions)) {
            return $.when([]);
        }

        const isArray = _.isArray(definitions);
        const serialized = _.map([].concat(definitions), function (definition) {
            return definition.resolvedAttributes();
        });

        const requestDefinitions = (isArray ? serialized : _.head(serialized));

        if (me._isBuffering) {
            const request = {
                attributes: attrs,
                deferred: new $.Deferred(),
                definitions: requestDefinitions
            };

            me._buffer.push(request);

            return request.deferred;
        }

        return fetchUniversalMetrics(attrs, requestDefinitions);
    };

    universal.flush = function () {
        const me = this;
        if (me._buffer) {
            me._buffer.trigger('fetch', me._buffer);
        }
    };

    universal.enableBuffering = true;

    universal.startBuffering = function () {
        const me = this;
        if (me.enableBuffering && !me._isBuffering) {
            me._buffer = createUniveralMetricsBuffer();
            me._isBuffering = true;
        }
    };

    universal.stopBuffering = function () {
        const me = this;
        me._isBuffering = false;
    };

});
